import React from "react"
import { CircleMarker, Marker, Polyline, Popup } from "react-leaflet"
import { DivIcon, LatLng } from "leaflet"

// A context-free track overlay shared by the app's `TrackView` and the simulator
// map. It owns the pure geometry (`TrackViewData`) and a `<TrackLayerView>` that
// renders a single track's `Polyline` + markers. It deliberately mounts NO
// `MapContainer`/tile layer and has NO app-context (`GetApp()`) dependency, so both
// the app (with i18n/theme/board-time) and the simulator (color-coded breadcrumbs)
// can drop it inside their own maps by passing formatting/color as props.

// The minimal parallel-array shape `trackGeometry` needs. Both the app's generated
// `ITrack` and the simulator's generated `ISimulatorShipTrack` satisfy it
// structurally, so neither bundle has to depend on the other's concrete type.
export interface TrackGeometryInput {
  lats?: number[];
  lngs?: number[];
  headings?: number[];
  timesUtc?: number[];
}

// The minimal shape `computeBounds` needs for a circle overlay (e.g. one ring of an
// anchor watch's swing circle) -- structural, like `TrackGeometryInput`, so callers
// don't have to depend on a concrete generated circle type. `radius` is in meters.
export interface CircleGeometryInput {
  lat: number;
  lng: number;
  radius: number;
}

// Meters per degree of latitude (WGS84-ish constant). Fine for a rough bounding-box
// expansion; not meant for precise navigation math.
const METERS_PER_DEGREE_LAT = 111320;

interface TrackPoint {
  lat: number;
  lng: number;
}

type TrackMarker =
  | { kind: "endpoint"; lat: number; lng: number; time: number }
  | { kind: "hour"; lat: number; lng: number; time: number; heading: number; size: number }
  | { kind: "direction"; lat: number; lng: number; heading: number };

export const TrackViewData = {
  hasPositions(tracks?: TrackGeometryInput[]): boolean {
    return (tracks || []).some((track) => TrackViewData.trackGeometry(track).points.length > 0);
  },

  // Order a track's sample indexes by their UTC time. Mirrors the original
  // in-place `toSorted` over the timesUtc array.
  sortedIndexes(track: TrackGeometryInput): number[] {
    return (track.timesUtc || [])
      .map((t, i) => ({ index: typeof i == "number" ? i : parseInt(i), time: t }))
      .toSorted((a, b) => a.time - b.time)
      .map((n) => n.index);
  },

  // Board-time hour for a track timestamp (seconds since 2001-01-01, matching the
  // app's reference epoch). `timezone` is the offset in seconds east of UTC; with
  // the default 0 this is the UTC hour.
  utcHour(t: number, timezone: number = 0): number {
    return new Date(Date.UTC(2001, 0, 1) + ((t + timezone) * 1000)).getUTCHours();
  },

  // Popup label for a marker: board time (Bordzeit) as the primary value with UTC
  // alongside, e.g. "14:32 (12:32 UTC)". When `timezone` is 0 board time equals UTC,
  // so only the single time is shown. `time` is seconds since the reference epoch
  // and `timezone` the offset in seconds east of UTC. `format` is `app.format`,
  // passed in so this stays pure/unit-testable. Shared by both marker types so they
  // render identically.
  popupLabel(time: number, timezone: number, format: (name: string, value: number) => string): string {
    const board = format("timeUtc", time + timezone);
    if (!timezone) {
      return board;
    }
    return `${board} (${format("timeUtc", time)} UTC)`;
  },

  // Walk a track in time order and produce the polyline points plus the marker
  // descriptors (endpoints, hourly ticks, and a single mid-track direction
  // indicator). Pure: no leaflet/JSX, so the geometry can be unit-tested.
  trackGeometry(track: TrackGeometryInput, timezone: number = 0): { points: TrackPoint[]; markers: TrackMarker[] } {
    const points: TrackPoint[] = [];
    const markers: TrackMarker[] = [];
    let lastHour = -1;
    let directionIndicator = false;
    const samples = TrackViewData.sortedIndexes(track).flatMap((i) => {
      const lat = track.lats?.[i];
      const lng = track.lngs?.[i];
      const time = track.timesUtc?.[i];
      if (lat == null || lng == null || time == null
        || !Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(time)
        || Math.abs(lat) > 90 || Math.abs(lng) > 180
        || (lat === 0 && lng === 0)) return [];
      const heading = track.headings?.[i];
      return [{ lat, lng, time, heading: heading != null && Number.isFinite(heading) ? heading : 0 }];
    });
    const length = samples.length;
    for (const [i, { lat, lng, time: t, heading }] of samples.entries()) {
      points.push({ lat, lng });
      const h = TrackViewData.utcHour(t, timezone);
      if (i == 0 || i == length - 1) {
        markers.push({ kind: "endpoint", lat, lng, time: t });
      } else if (h > lastHour) {
        const s = h % 4 == 0 ? 12 : 8;
        markers.push({ kind: "hour", lat, lng, time: t, heading, size: s });
        lastHour = h;
      }
      if (!directionIndicator && i >= (length / 2)) {
        directionIndicator = true;
        markers.push({ kind: "direction", lat, lng, heading });
      }
    }
    return { points, markers };
  },

  // Reduce every track's usable points to a min/max lat/lng bounding box.
  // `circles` (e.g. an anchor watch's swing-circle
  // rings) are optionally folded in too, widening the box so a short/just-started
  // track still zooms out to show the full circle instead of collapsing onto its
  // own (possibly single-point) extent.
  computeBounds(tracks?: TrackGeometryInput[], circles?: CircleGeometryInput[]): [[number?, number?], [number?, number?]] {
    const bounds: [[number?, number?], [number?, number?]] = [[undefined, undefined], [undefined, undefined]];
    const expand = (lat: number, lng: number) => {
      if (bounds[0][0] === undefined || lat < bounds[0][0]) {
        bounds[0][0] = lat;
      }
      if (bounds[1][0] === undefined || lat > bounds[1][0]) {
        bounds[1][0] = lat;
      }
      if (bounds[0][1] === undefined || lng < bounds[0][1]) {
        bounds[0][1] = lng;
      }
      if (bounds[1][1] === undefined || lng > bounds[1][1]) {
        bounds[1][1] = lng;
      }
    };
    for (const track of tracks || []) {
      for (const { lat, lng } of TrackViewData.trackGeometry(track).points) {
        expand(lat, lng);
      }
    }
    for (const circle of circles || []) {
      // Rough meters -> degrees conversion (WGS84-ish constant); precise enough for
      // a bounding-box expansion, not for navigation math.
      const dLat = circle.radius / METERS_PER_DEGREE_LAT;
      const dLng = circle.radius / (METERS_PER_DEGREE_LAT * Math.cos(circle.lat * Math.PI / 180) || 1);
      expand(circle.lat - dLat, circle.lng - dLng);
      expand(circle.lat + dLat, circle.lng + dLng);
    }
    return bounds;
  },
};

interface TrackLayerViewProps {
  track: TrackGeometryInput;
  // Polyline color; the app uses a fixed color and the simulator a per-ship color.
  color: string;
  // Polyline stroke width (also the direction-indicator icon size). Defaults to 6.
  weight?: number;
  // Optional dash pattern (leaflet's Path `dashArray`, e.g. "6 6") so a track can be
  // styled distinctly from a solid line -- e.g. the app's anchor track vs. the main
  // passage track. Omitted (undefined) renders a solid line, matching prior behavior.
  dashArray?: string;
  // Board-time offset in seconds east of UTC, applied to hour bucketing + popups.
  timezone?: number;
  // Which markers to draw: "full" = endpoints + hourly ticks + a direction
  // indicator (the app's behavior); "none" = just the color-coded polyline (the
  // simulator's breadcrumb).
  markers?: "full" | "none";
  // Optional popup label for endpoint/hour markers; when omitted (e.g. the
  // simulator, which has no i18n `app.format`) markers render without a `Popup`.
  popupLabel?: (time: number) => string;
  // Key prefix so multiple layers in one map keep distinct React keys.
  keyPrefix?: string;
}

// Render a single track as a `Polyline` plus (optionally) its markers, using only
// props for formatting/color. Must be mounted inside a react-leaflet map.
export function TrackLayerView({ track, color, weight = 6, dashArray, timezone = 0, markers = "full", popupLabel, keyPrefix = "track" }: TrackLayerViewProps) {
  const { points, markers: descriptors } = TrackViewData.trackGeometry(track, timezone);
  if (points.length == 0) {
    return null;
  }

  const nodes: React.ReactNode[] = [];
  if (markers == "full") {
    for (const [dIndex, d] of descriptors.entries()) {
      const markerKey = `${keyPrefix}-marker-${dIndex}`;
      if (d.kind == "endpoint") {
        nodes.push(
          <CircleMarker key={markerKey} center={[d.lat, d.lng]} pathOptions={{ color: "white", weight: 2 }} radius={4}>
            {popupLabel && <Popup>{popupLabel(d.time)}</Popup>}
          </CircleMarker>,
        );
      } else if (d.kind == "hour") {
        const icon = new DivIcon({ html: `<div class="track-line-inner hour-${TrackViewData.utcHour(d.time, timezone)}" style="transform: rotate(${d.heading}deg);"></div>`, className: "track-line", iconSize: [d.size, d.size], iconAnchor: [d.size / 2, 1] });
        nodes.push(
          <Marker key={markerKey} icon={icon} position={[d.lat, d.lng]}>
            {popupLabel && <Popup>{popupLabel(d.time)}</Popup>}
          </Marker>,
        );
      } else {
        const icon = new DivIcon({ html: `<div class="track-direction-indicator-inner" style="transform: rotate(${d.heading + 180}deg);">
          <svg
            width="8"
            height="8"
            viewBox="0 0 100 100"
            version="1.1"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 0 L50 100 L100 0 Z" fill="#fff"></path>
          </svg>
        </div>`, className: "track-direction-indicator", iconSize: [weight, weight], iconAnchor: [4, 7] });
        nodes.push(<Marker key={markerKey} icon={icon} position={[d.lat, d.lng]} />);
      }
    }
  }

  return (
    <>
      <Polyline key={`${keyPrefix}-line`} pathOptions={{ color, weight, dashArray }} positions={points.map((p) => new LatLng(p.lat, p.lng))} />
      {nodes}
    </>
  );
}
